import React, { useMemo, useState } from 'react';
import { AbsenceFilesPlaceholderIcon, Box, formatSnakeCaseToGeneral, Inscription, NoSearchItemsPlaceholder, Option, PaginationBar, Preloader, SearchInput, SearchPlaceholderIcon, SelectDropdown } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import { ALL_OPTION, ALL_OPTION_KEY, DEFAULT_DATE_TIME_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import { Actions } from '@/modules/ActivityLog/LogList/useGetActivityLogs.query';
import { useListPagination } from '@/modules/employer/hooks';
import stateAllToggling from '@/utils/handlers/dropdownStateAllToggling';

import { useGetChangesetAttributesQuery } from './queries/useGetChangesetAttributes.query';
import { Changeset } from './queries/useGetContributionChangesets.query';
import { Change, useSearchChangesetChangesQuery } from './queries/useSearchChangesetChanges.query';
import { ExpandableHeader } from './ExpandableHeader';
import { GroupedChangeSetLogs } from './GroupedChangeSetLogs';

export const PER_PAGE_OPTIONS=['5', '10', '20', '50', '100'];
export const DEFAULT_PER_PAGE = 5;
const DEFAULT_PAGE = 1;

interface Props {
  changeSet?: Changeset;
}

export const ChangeSet: React.FC<Props> = ({ changeSet }) => {
  const isBulkUpdate = useMemo(() => changeSet?.actionType === Actions.bulkUpdate, [changeSet]);
  const actionTypeTitle = useMemo(() => {
    if (changeSet?.actionType === Actions.bulkUpdate) {
      return 'Update';
    }
    return capitalize(formatSnakeCaseToGeneral(changeSet?.actionType || EMPTY_FIELD_VALUE));
  }, [changeSet]);
  const [isExpanded, setExpanded] = useState(false);
  const enabled = useMemo(() => Boolean(isExpanded
    && !isBulkUpdate
    && changeSet?.changesCount
    && changeSet?.changesCount > 1),
    [isExpanded, changeSet, isBulkUpdate]);
  const { attributes } = useGetChangesetAttributesQuery(changeSet?.id, enabled);
  const attributesOptions = useMemo<Option[]>(() => {
    if (!attributes?.length) return [];

    return [
      ALL_OPTION,
      ...attributes.map((item) => ({
        key: item?.attributeName,
        value: item?.attributeName,
        title: item?.label,
      })),
    ] as Option[];
  }, [attributes]);
  const {
    page,
    perPage,
    currentSearch,
    filterParams,
    setPage,
    setPerPage,
    searchString,
    setSearchString,
    setFilterParams,
  } = useListPagination(DEFAULT_PER_PAGE);
  const filterParamsValues = useMemo(() => stateAllToggling(filterParams), [filterParams]);
  const hasFilterParams = useMemo(
    () => filterParamsValues.length > 0
      && !filterParamsValues.some((param) => param === ALL_OPTION_KEY),
    [filterParamsValues],
  );
  const { formattedData: {
    content, totalElements,
  }, isLoading } = useSearchChangesetChangesQuery(changeSet?.id, isExpanded, {
    ...isBulkUpdate ? {} : {
      page: page - 1,
      size: perPage,
      ...hasFilterParams && filterParamsValues?.length ? {
        fieldNames: filterParamsValues as string[],
      } : {},
      ...currentSearch && enabled ? {
        search: currentSearch,
      } : {},
    },
  });
  const hasStatusField = useMemo(() => content?.some((item) => item?.fieldName === 'Contribution status'),
    [content]);
  const hasNoFiltering = useMemo(() => (changeSet?.changesCount && changeSet?.changesCount === 1)
    || hasStatusField || isBulkUpdate,
    [hasStatusField, isBulkUpdate, changeSet]);
  const groupedLogs = useMemo<Change[][]>(() => {
    if (!content?.length) return [];

    const grouped = content.reduce<Record<string, Change[]>>((acc, change) => {
      const uniqueGroupId = `${change?.uniqueGroupId}`;
      if (!acc[uniqueGroupId]) {
        acc[uniqueGroupId] = [];
      }
      acc[uniqueGroupId].push(change);
      return acc;
    }, {});

    return Object.values(grouped);
  }, [content]);

  const [testId, dateOfChange, isCreating] = useMemo(() => [
    `ChangesSet_${changeSet?.id}`,
    changeSet?.changedAt ? dayjs(changeSet.changedAt).format(DEFAULT_DATE_TIME_FORMAT) : EMPTY_FIELD_VALUE,
    changeSet?.actionType === Actions.create || changeSet?.actionType === Actions.createFromTemplate,
  ], [changeSet]);

  return (
    <Box
      data-testId={testId}
      pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
      background="canvas"
      round="container2Round"
      margin={{ top: 'spacing8' }}
      border={{ color: 'border2', size: 'medium' }}
      height={{ min: '72px' }}
      justify="center"
    >
      <Box
        direction="row"
        align="center"
      >
        <Box flex>
          {isCreating ? (
            <Box margin={{ left: '40px' }}>
              <Inscription>
                {dateOfChange}
              </Inscription>
            </Box>
          ) : (
            <ExpandableHeader
              testId={changeSet?.id}
              setExpanded={setExpanded}
              isExpanded={isExpanded}
            >
              <Box margin={{ left: 'spacing16' }}>
                <Inscription>
                  {dateOfChange}
                </Inscription>
              </Box>
            </ExpandableHeader>
          )}
        </Box>

        <Box flex>
          <Inscription weight="bold" data-testId={`${testId}_Changed_by_name`}>
            {changeSet?.changedByName || EMPTY_FIELD_VALUE}
          </Inscription>
        </Box>

        <Box flex pad={{ left: 'spacing12' }}>
          <Inscription data-testId={`${testId}_Action_type`}>
            {actionTypeTitle}
          </Inscription>
        </Box>

        <Box flex pad={{ left: 'spacing12' }}>
          {isCreating ? null : (
            <Inscription data-testId={`${testId}_Changes_count`}>
              {changeSet?.changesCount || EMPTY_FIELD_VALUE}
            </Inscription>
          )}
        </Box>
      </Box>

      {isExpanded ? (
        <Box
          background="module"
          round="container1Round"
          pad="spacing24"
          margin={{ top: 'spacing8' }}
        >
          {hasNoFiltering ? null : (
            <Box direction="row" align="center" justify="between" margin={{ bottom: 'spacing12' }}>
              <Box>
                <Inscription
                  weight="bold"
                  data-testId={`${testId}_employee_records`}
                >
                  Employee records: {changeSet?.updatedEmployeesNumber ?? 0}
                </Inscription>
              </Box>
              <Box direction="row" align="center">
                <SearchInput
                  hasSearchingHistory
                  testId={testId}
                  value={searchString}
                  placeholder="Search"
                  onChange={setSearchString}
                  disabled={!enabled}
                />
                <Box margin={{ left: 'spacing12' }} width={{ min: 'control', max: 'control' }}>
                  <SelectDropdown
                    id="attribute"
                    allMode
                    testId={testId}
                    ellipsisMode
                    activeTitle
                    name="Attribute"
                    prefix="Attribute"
                    options={attributesOptions}
                    values={filterParamsValues}
                    onChangeValues={(val) => {
                      setFilterParams(val);
                      setPage(DEFAULT_PAGE);
                      setSearchString('');
                    }}
                    disabled={!enabled}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {isLoading ? (
            <Box margin="spacing48">
              <Preloader testId="ChangeSetLogs" />
            </Box>
          ) : null}

          {!isLoading && totalElements === 0 ? (
            <NoSearchItemsPlaceholder
              placeholderTitle={hasFilterParams || currentSearch ? 'No results have been found' : 'There are no results yet'}
              node={
                hasFilterParams || currentSearch ? (
                  <SearchPlaceholderIcon size="xxlarge" color="iconSecondary" />
                ) : (
                  <AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />
                )
              }
              description={
                hasFilterParams || currentSearch
                  ? 'Try to adjust your search or filter to find what you\'re looking for.'
                  : ''
              }
            />
          ) : <GroupedChangeSetLogs groupedLogs={groupedLogs} isBulkUpdate={isBulkUpdate} />}

          {hasNoFiltering ? null : (
            <PaginationBar
              page={page}
              total={totalElements || 0}
              pageSize={perPage}
              onChangePage={setPage}
              onChangePageSize={setPerPage}
              perPageOptions={PER_PAGE_OPTIONS}
            />
          )}
        </Box>
      ) : null}
    </Box>
  );
};
